<script lang="ts">
import { I3pService } from '@nuxtDatakodeUtils/services/i3p.service'
import SelectEpci from './SelectEpci.vue'
import SelectCity from './SelectCity.vue'
import { useUserStore } from '~/store'
import { MajicEventEnum } from '~/enum/MajicEvent.enum'

export default {
    name: 'CitiesGeojson',
    components: { SelectEpci, SelectCity },
    props: {
        data: { type: Object, required: true },
        dataKey: { type: String, required: false, default: null },
        edit: { type: Boolean, required: true },
        validation: { type: Object, required: false, default: () => { return {} } },
        i3pService: { type: I3pService, required: true },
    },
    emits: ['updateData'],
    data() {
        const user = useUserStore()
        return {
            selectedCities: [],
            citiesGeojson: null,
            user,
            MAX_VISIBLE: 3,
        }
    },
    computed: {
        visibleCities(): [] {
            return this.selectedCities.slice(0, this.MAX_VISIBLE)
        },
        hiddenCities(): [] {
            return this.selectedCities.slice(this.MAX_VISIBLE)
        },
    },
    watch: {
        'data.communes': function () {
            if (this.data) {
                this.selectedCities = this.data.communes
                this.updateSelectedCities()
            }
        },
    },
    async mounted() {
        await this.getCitiesGeojson()
        if (this.data) {
            this.selectedCities = this.data.communes
            this.updateSelectedCities()
        }

        this.i3pService.emitter
            .on(MajicEventEnum.selectedCommune, (event: { insee: string }) => {
                if (event.insee)
                    this.updateCitiesByInsee([event.insee])
            })
    },
    methods: {
        updateCitiesByInsee(data: []) {
            data?.forEach((insee: number) => {
                const isAlreadyInSelectedCities = this.selectedCities.find((city: any) => city.insee === insee)

                if (!isAlreadyInSelectedCities) {
                    const feature = this.citiesGeojson.features.find((feature: any) => feature.properties.insee === insee)
                    if (feature)
                        this.selectedCities = [...this.selectedCities, feature.properties] // .push(feature.properties)
                }
                else {
                    this.selectedCities = this.selectedCities.filter(
                        (city: any) => city.insee !== insee,
                    )
                }
            })
            this.updateSelectedCities()
        },
        updateSelectedCities(): void {
            this.getCitiesGeojson()
            this.i3pService.emitter.emit(MajicEventEnum.selectedCommunesUpdated, { selectedCities: this.selectedCities.map((city: any) => city.insee) })
            this.$emit('updateData', this.selectedCities)
        },
        getCitiesGeojson(): void {
            this.citiesGeojson = this.i3pService.geojsonData
        },
        setSelectedCities(prop) {
            const cityIsInArray = this.selectedCities.find(city => city.insee === prop.insee)
            if (cityIsInArray)
                this.deleteCity(prop)
            else
                this.selectedCities.push(prop)

            this.updateSelectedCities()
        },
        deleteCity(prop) {
            this.selectedCities = this.selectedCities.filter(city => city.insee !== prop.insee)
            this.updateSelectedCities()
        },
        deleteAllCities() {
            this.selectedCities = []
            this.updateSelectedCities()
        },
        addAllCities() {
            if (this.citiesGeojson) {
                this.selectedCities = this.citiesGeojson.features.map(feature => feature.properties)
                this.updateSelectedCities()
            }
        },
    },
}
</script>

<template>
    <div>
        <template v-if="!edit">
            {{ `${selectedCities.length} commune${selectedCities.length > 1 ? 's' : ''}` }}
        </template>
        <!-- EPCI -->
        <template v-else>
            <SelectEpci v-if="user.isAdmin" @update-cities-by-insee="updateCitiesByInsee($event)" />

            <SelectCity @update-cities-by-insee="updateCitiesByInsee($event)" />
        </template>

        <v-row
            v-if="edit && citiesGeojson"
            class="my-2"
        >
            <v-col cols="12" sm="6">
                <v-btn
                    color="secondary" variant="text" size="small" prepend-icon="mdi-plus"
                    @click="addAllCities()"
                >
                    Ajouter toutes les communes
                </v-btn>
            </v-col>
            <v-col cols="12" sm="6">
                <v-btn
                    color="danger" variant="text" size="small" prepend-icon="mdi-delete"
                    @click="deleteAllCities()"
                >
                    Supprimer la sélection
                </v-btn>
            </v-col>
        </v-row>
        <br>
        <div class="d-flex flex-wrap align-center">
            <!-- First 3 cities -->
            <v-chip
                v-for="city in visibleCities"
                :key="city.insee"
                size="small"
                variant="flat"
                class="font-weight-bold ma-1"
                :closable="edit"
                @click:close="deleteCity(city)"
            >
                {{ city.v || `${city.name} (${city.insee})` }}
            </v-chip>

            <!-- If there are more -->
            <v-expansion-panels
                v-if="hiddenCities.length"
                class="ma-1 w-100"
                variant="accordion"
                elevation="0"
            >
                <v-expansion-panel>
                    <v-expansion-panel-title class="px-2 py-1 text-body-2">
                        +{{ hiddenCities.length }} autres
                    </v-expansion-panel-title>

                    <v-expansion-panel-text class="px-0">
                        <div class="d-flex flex-wrap">
                            <v-chip
                                v-for="city in hiddenCities"
                                :key="city.insee"
                                size="small"
                                variant="flat"
                                class="font-weight-bold ma-1"
                                :closable="edit"
                                @click:close="deleteCity(city)"
                            >
                                {{ city.v || `${city.name} (${city.insee})` }}
                            </v-chip>
                        </div>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>
    </div>
</template>
